import * as React from 'react'
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  Pagination, Chip, Typography, FormControl, Select, MenuItem,
} from '@mui/material'
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined'
import {dataGridWrapSx, headerCellSx, cellSx, alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, PURPLE} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {STATUS_TONE} from '../data/reference'
import {kg, date, int, dims} from '../format'
import {isPreliminary, PRELIM_CYCLES, packOf} from './logic'
import {PRELIM_HELP} from './copy'
import {chipSx} from '../components/tone'
import EmptyState from '../components/EmptyState'
import RqLevelChip from './RqLevelChip'
import RqCyclePips from './RqCyclePips'
import RqApprovedDrawer from './RqApprovedDrawer'

const PER_PAGE = 10
const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const BRANDING_LABEL = {self: 'Self branded', generic: 'Generic'}
// The standing filter's closed control already renders at SIZE['2xs'], so its
// portalled menu needs the same override (sdd/type-parity-spec.md).
const selectMenuProps = {PaperProps: {sx: {'& .MuiMenuItem-root': {fontSize: SIZE['2xs']}}}}

/** Field accessors for each sortable column — read once per row, never inside the comparator. */
const SORT_ACCESSOR = {
  id: (r) => r.id,
  level: (r) => r.level,
  subject: (r) => r.subject,
  cyclesElapsed: (r) => r.cyclesElapsed || 0,
  deadKg: (r) => r.deadKg || 0,
  volKg: (r) => r.volKg || 0,
  chargeKg: (r) => r.chargeKg || 0,
  effectiveFrom: (r) => r.effectiveFrom || '',
}

const COLUMNS = [
  {key: 'id', label: 'Request', width: 112},
  {key: 'level', label: 'Level', width: 124},
  {key: 'subject', label: 'Frozen for'},
  {key: 'cyclesElapsed', label: 'Standing', width: 156},
  {key: 'deadKg', label: 'Dead', width: 86, align: 'right'},
  {key: 'volKg', label: 'Volumetric', width: 100, align: 'right'},
  {key: 'chargeKg', label: 'Chargeable', width: 104, align: 'right'},
  {key: null, label: 'Packing material', width: 176},
  {key: 'effectiveFrom', label: 'Effective from', width: 136},
]

function prelimBannerLead(n) {
  if (n === 0) return 'No freeze is preliminary right now.'
  return `${int(n)} freeze${n === 1 ? '' : 's'} ${n === 1 ? 'is' : 'are'} preliminary.`
}

/**
 * Weight Freeze (v2) — Requests: the approved tab.
 *
 * Plain MUI Table + TableSortLabel + Pagination, matching
 * catalogue/CatalogueTable.js and initiate/IniCandidateTable.js — NOT
 * DataGrid, because Standing is a composite cell (a chip over RqCyclePips)
 * and sorts on the derived `cyclesElapsed`, and Packing material is a
 * label-over-subline composite too. There is no row selection in this tab,
 * so none of the selection hazards the other two tables guard against apply
 * here.
 *
 * Sort, the standing filter, pagination and the drawer's open id are ALL
 * owned locally — `rows` is the already search/level-filtered set the
 * section hands down (see RequestsSection.js's own `filterRequests`), and
 * this component never mutates anything; opening the drawer is pure
 * read-only navigation into one row's own record.
 */
export default function RqApproved({rows, packs, onNavigate, focusFallbackRef}) {
  const [standing, setStanding] = React.useState('all')
  const [sort, setSort] = React.useState({key: 'effectiveFrom', dir: 'desc'})
  const [page, setPage] = React.useState(1)
  const [openId, setOpenId] = React.useState(null)

  const prelimCount = rows.filter(isPreliminary).length
  const forceCount = rows.length - prelimCount

  const view = React.useMemo(() => rows.filter((r) => {
    if (standing === 'prelim') return isPreliminary(r)
    if (standing === 'force') return !isPreliminary(r)
    return true
  }), [rows, standing])

  // Resets on a standing-filter change or a genuinely new incoming `rows`
  // identity (a search/level change upstream) — never on a re-sort — or a
  // filter that shortens the list would strand the merchant on an empty page.
  React.useEffect(() => { setPage(1) }, [rows, standing])

  const sortedRows = React.useMemo(() => {
    const accessor = SORT_ACCESSOR[sort.key] || SORT_ACCESSOR.effectiveFrom
    const list = [...view]
    list.sort((a, b) => {
      const av = accessor(a)
      const bv = accessor(b)
      if (av < bv) return -1
      if (av > bv) return 1
      return 0
    })
    if (sort.dir === 'desc') list.reverse()
    return list
  }, [view, sort])

  const handleSort = (key) => setSort((s) => (
    s.key === key ? {key, dir: s.dir === 'desc' ? 'asc' : 'desc'} : {key, dir: 'desc'}
  ))

  const pages = Math.max(1, Math.ceil(sortedRows.length / PER_PAGE))
  const clampedPage = Math.min(page, pages)
  const pageRows = sortedRows.slice((clampedPage - 1) * PER_PAGE, clampedPage * PER_PAGE)

  const openReq = openId ? (rows.find((r) => r.id === openId) || null) : null

  // Same focus-recovery fallback every other drawer in this module uses
  // (see catalogue/CataloguePage.js's closeMergeDrawer and friends): the
  // trigger here is a TableRow, which the standing filter or a search typed
  // while the drawer is open can hide out from under MUI's own focus
  // restore, landing it on <body>. Reused, not reinvented.
  const closeDrawer = React.useCallback(() => {
    setOpenId(null)
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  return (
    <Box sx={{display: 'grid', gap: GAP.sm}}>
      <Box sx={{...alertStripSx(PURPLE), alignItems: 'flex-start'}}>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
          <Box component='span' sx={{fontWeight: WEIGHT.bold}}>{prelimBannerLead(prelimCount)}</Box>{' '}
          {PRELIM_HELP} Past those {PRELIM_CYCLES} cycles a freeze is fully in force and bills without that caveat.
        </Typography>
      </Box>

      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap'}}>
        <FormControl size='small' sx={{minWidth: 220}}>
          <Select
            value={standing}
            onChange={(event) => setStanding(event.target.value)}
            inputProps={{'aria-label': 'Filter by standing'}}
            MenuProps={selectMenuProps}
            sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}
          >
            <MenuItem value='all'>All approved ({int(rows.length)})</MenuItem>
            <MenuItem value='prelim'>Preliminary only ({int(prelimCount)})</MenuItem>
            <MenuItem value='force'>Fully in force ({int(forceCount)})</MenuItem>
          </Select>
        </FormControl>
        <Typography sx={subtleTextSx}>Select a row for the full record</Typography>
      </Box>

      {view.length === 0 ? (
        <Box sx={dataGridWrapSx}>
          <EmptyState
            icon={SearchOffOutlinedIcon}
            title='Nothing matches those filters'
            body='No approved weight freeze matches the current search and filter combination.'
          />
        </Box>
      ) : (
        <Box sx={dataGridWrapSx}>
          <Table sx={{minWidth: 1080}}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.label}
                    sx={{...headerCellSx, textAlign: col.align, width: col.width}}
                  >
                    {col.key ? (
                      <TableSortLabel
                        active={sort.key === col.key}
                        direction={sort.key === col.key ? sort.dir : 'desc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((r) => {
                const volGoverns = (r.volKg || 0) >= (r.deadKg || 0)
                const pack = packOf(packs, r.packId)

                return (
                  <TableRow key={r.id} hover onClick={() => setOpenId(r.id)} sx={{cursor: 'pointer'}}>
                    <TableCell sx={cellSx}>
                      <Typography sx={{fontFamily: 'monospace', fontSize: SIZE.xs, color: SUBTLE}}>{r.id}</Typography>
                    </TableCell>

                    <TableCell sx={cellSx}><RqLevelChip level={r.level} /></TableCell>

                    <TableCell sx={cellSx}>
                      <Typography
                        title={r.subject}
                        sx={{
                          fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY,
                          maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {r.subject}
                      </Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Box sx={{display: 'grid', gap: 0.35}}>
                        <Chip
                          size='small'
                          label={isPreliminary(r) ? 'Preliminary' : 'Fully in force'}
                          sx={chipSx(isPreliminary(r) ? STATUS_TONE.preliminary : STATUS_TONE.approved)}
                        />
                        <RqCyclePips cycles={r.cyclesElapsed} />
                      </Box>
                    </TableCell>

                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: volGoverns ? SUBTLE : NAVY}}>
                        {kg(r.deadKg)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: volGoverns ? NAVY : SUBTLE}}>
                        {kg(r.volKg)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
                        {kg(r.chargeKg)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={cellSx}>
                      {pack ? (
                        <>
                          <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{pack.label}</Typography>
                          <Typography sx={subtleTextSx}>
                            {BRANDING_LABEL[pack.branding] || pack.branding} · {pack.dims ? dims(pack.dims) : 'No dims'}
                          </Typography>
                        </>
                      ) : <Typography sx={subtleTextSx}>Not set</Typography>}
                    </TableCell>

                    <TableCell sx={cellSx}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{date(r.effectiveFrom)}</Typography>
                      <Typography sx={subtleTextSx}>Approved by {r.approvedBy}</Typography>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          {pages > 1 && (
            <Box sx={{display: 'flex', justifyContent: 'flex-end', p: 1.5}}>
              <Pagination
                size='small'
                page={clampedPage}
                count={pages}
                onChange={(event, p) => setPage(p)}
                sx={{'& .MuiPaginationItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}}
              />
            </Box>
          )}
        </Box>
      )}

      <RqApprovedDrawer
        req={openReq}
        packs={packs}
        open={openReq !== null}
        onClose={closeDrawer}
        onNavigate={onNavigate}
      />
    </Box>
  )
}
